import React from 'react';

/**
 * SmartphoneMockup — iPhone 15 Pro style
 *
 * Pure-CSS device frame. Height is controlled by `heightCss`; width
 * auto-derives from the 9:19.5 body aspect ratio so it always stays
 * proportional and the bottom is never clipped.
 *
 * Props:
 *   children   – rendered inside the screen viewport
 *   heightCss  – CSS length for device height (default: "min(78dvh, 660px)")
 */
const SmartphoneMockup = ({ children, heightCss = 'min(78dvh, 660px)' }) => {
  const w = `calc(${heightCss} * 9 / 19.5)`;

  return (
    <div
      className='relative mx-auto flex-shrink-0 select-none'
      style={{
        height: heightCss,
        width: w,
      }}
    >
      {/* ── Side buttons ──────────────────────────────────────────────────── */}

      {/* Volume up */}
      <div
        className='absolute'
        style={{
          left: '-3px',
          top: '18%',
          width: '3px',
          height: '8%',
          borderRadius: '2px 0 0 2px',
          background: 'linear-gradient(180deg, #8a8a8a 0%, #b0b0b0 50%, #8a8a8a 100%)',
          boxShadow: '-1px 0 2px rgba(0,0,0,0.4)',
        }}
      />
      {/* Volume down */}
      <div
        className='absolute'
        style={{
          left: '-3px',
          top: '28%',
          width: '3px',
          height: '8%',
          borderRadius: '2px 0 0 2px',
          background: 'linear-gradient(180deg, #8a8a8a 0%, #b0b0b0 50%, #8a8a8a 100%)',
          boxShadow: '-1px 0 2px rgba(0,0,0,0.4)',
        }}
      />
      {/* Silent toggle */}
      <div
        className='absolute'
        style={{
          left: '-3px',
          top: '11%',
          width: '3px',
          height: '4.5%',
          borderRadius: '2px 0 0 2px',
          background: 'linear-gradient(180deg, #8a8a8a 0%, #b0b0b0 50%, #8a8a8a 100%)',
          boxShadow: '-1px 0 2px rgba(0,0,0,0.4)',
        }}
      />
      {/* Power button */}
      <div
        className='absolute'
        style={{
          right: '-3px',
          top: '20%',
          width: '3px',
          height: '12%',
          borderRadius: '0 2px 2px 0',
          background: 'linear-gradient(180deg, #8a8a8a 0%, #b0b0b0 50%, #8a8a8a 100%)',
          boxShadow: '1px 0 2px rgba(0,0,0,0.4)',
        }}
      />

      {/* ── Outer titanium frame ───────────────────────────────────────────── */}
      <div
        className='absolute inset-0'
        style={{
          borderRadius: '3rem',
          // Brushed titanium: subtle angular gradient
          background:
            'linear-gradient(145deg, #a0a0a0 0%, #c8c8c8 20%, #909090 40%, #bcbcbc 60%, #888888 80%, #b0b0b0 100%)',
          boxShadow: [
            // Outer ambient drop-shadow
            '0 32px 64px rgba(0,0,0,0.5)',
            '0 8px 24px rgba(0,0,0,0.35)',
            // Highlight on top-left edge
            'inset 0 1px 0 rgba(255,255,255,0.55)',
            // Shadow on bottom-right edge
            'inset 0 -1px 0 rgba(0,0,0,0.3)',
          ].join(', '),
          padding: '3px', // frame thickness
        }}
      >
        {/* ── Inner black bezel ──────────────────────────────────────────── */}
        <div
          className='relative flex h-full w-full flex-col overflow-hidden'
          style={{
            borderRadius: '2.75rem',
            background: '#0a0a0a',
            padding: '10px',
          }}
        >
          {/* ── Dynamic Island ──────────────────────────────────────────── */}
          <div className='relative mb-1 flex flex-shrink-0 items-center justify-center' style={{ height: '30px' }}>
            <div
              style={{
                width: '34%',
                height: '28px',
                borderRadius: '20px',
                background: '#000',
                // Subtle inner rim
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06), 0 0 0 1px rgba(0,0,0,0.8)',
                position: 'relative',
              }}
            >
              {/* Tiny camera dot inside the island */}
              <div
                style={{
                  position: 'absolute',
                  right: '18%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#1a1a2e',
                  boxShadow: 'inset 0 0 0 2px #0d0d1a, 0 0 4px rgba(100,180,255,0.25)',
                }}
              />
            </div>
          </div>

          {/* ── Screen content ──────────────────────────────────────────── */}
          <div
            className='relative flex-1 overflow-hidden'
            style={{
              borderRadius: '2.25rem',
              background: '#000',
              // Screen-glass glare: subtle top highlight
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
            }}
          >
            {children}

            {/* Glass glare overlay — non-interactive */}
            <div
              className='pointer-events-none absolute inset-0'
              style={{
                borderRadius: '2.25rem',
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%)',
              }}
            />
          </div>

          {/* ── Home indicator ──────────────────────────────────────────── */}
          <div className='flex flex-shrink-0 items-center justify-center' style={{ height: '22px' }}>
            <div
              style={{
                width: '38%',
                height: '4px',
                borderRadius: '2px',
                background: 'rgba(255,255,255,0.28)',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartphoneMockup;
